




































































import Vue from 'vue';
import { defineComponent, computed, reactive, ref, onBeforeMount, watch } from '@vue/composition-api';
import { parseISO, format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isBefore, addDays, addMonths, subMonths, setYear, isEqual } from 'date-fns';
import { CalendarDay } from '../abstractions';
import { generateDaysNames } from '../helpers';

export default defineComponent({
    name: 'WokDatePicker',

    props: {
        value: {
            type: String,
            default: ''
        },
        error: {
            type: Object,
            default: null
        },
        locale: {
            type: Object,
            default: null
        }
    },

    model: { prop: 'value', event: 'update:value' },

    setup(props, context) {
        const state = reactive({
            showYears: false,
            displayMonth: new Date(),
            displayYear: 0,
            pickerPosition: 'bottom'
        });

        // We `props.value` changes we need to set `state.displayMonth` and
        // `state.displayYear` to make sure the calendar shows the correct
        // page for the changed value.
        watch(
            () => props.value,
            () => {
                let date = props.value ? parseISO(props.value) : state.displayMonth;
                if (!isEqual(date, state.displayMonth)) {
                    state.displayMonth = startOfMonth(date);
                    state.displayYear = date.getFullYear();
                }
            },
            { immediate: true }
        );

        const selectedDay = computed(() => {
            return props.value ? parseISO(props.value) : null;
        });

        const headerYear = computed(() => {
            return format(state.displayMonth, "yyyy");
        });

        const headerMonth = computed(() => {
            return format(state.displayMonth, "MMMM", { locale: props.locale?.date });
        });

        const headerDecade = computed(() => {
            let decade = Math.floor(state.displayYear / 10) * 10;
            return `${decade} - ${decade + 19}`;
        });

        const headerTitle = computed(() => {
            if (state.showYears) {
                return headerDecade.value;
            }
            else {
                return `${headerMonth.value} ${headerYear.value}`;
            }
        });

        const calendarWeeks = computed(() => {
            let m = startOfMonth(state.displayMonth);
            let s = startOfWeek(m, { locale: props.locale?.date, weekStartsOn: 1 });
            let e = endOfWeek(endOfMonth(m));

            let weeks = [];

            for (let n = 0, d = s ; n < 6 ; n++) {
                let w: Array<CalendarDay> = [];
                for (let i = 0 ; i < 7 ; i++) {
                    w.push(new CalendarDay(d, m, <Date>selectedDay.value));
                    d = addDays(d, 1);
                }
                weeks.push(w);
            }

            return weeks;
        });

        const calendarYears = computed(() => {
            let years = [];
            let startYear = Math.floor(state.displayYear/10) * 10;
            for (let i=0; i < 20; i++) {
                years.push(startYear + i);
            }
            return years;
        });

        const daysNames = computed(() => {
            return generateDaysNames(state.displayMonth, props.locale?.date);
        });

        const calendarDayClasses = (d: CalendarDay) => {
            return {
                'wok--picker-input-date__calendar-day': true,
                'wok--picker-input-date__calendar-day--not-current-month': !d.isThisMonth,
                'wok--picker-input-date__calendar-day--current-day' : d.isSelectedDay,
                'wok--picker-input-date__calendar-day--today' : d.isToday
            }
        };

        const prevMonth = () => {
            state.displayMonth = subMonths(state.displayMonth, 1);
        };

        const nextMonth = () => {
            state.displayMonth = addMonths(state.displayMonth, 1);
        };

        const prevDecades = () => {
            state.displayYear = state.displayYear - 20 ;
        };

        const nextDecades = () => {
            state.displayYear = state.displayYear + 20 ;
        };

        const onPrevClick = () => {
            if (state.showYears) {
                prevDecades();
            }
            else {
                prevMonth();
            }
        };

        const onNextClick = () => {
            if (state.showYears) {
                nextDecades();
            }
            else {
                nextMonth();
            }
        };

        const onTitleClick = () => {
            if (!state.showYears) {
                state.showYears = true;
            }
            else {
                return;
            }
        };

        const setSelectedDate = (evt: Event) => {
            if (evt.target instanceof HTMLElement) {
                let date = evt.target.getAttribute("data-date");
                if (date && date !== props.value) {
                    context.emit('update:value', date);
                }
                context.emit('hide-picker');
            }
        }

        const setSelectedYear = (evt: Event) => {
            if (evt.target instanceof HTMLElement) {
                let year = parseInt(evt.target.getAttribute("data-year")!);
                if (year && year !== state.displayYear) {
                    state.displayYear = year;
                    state.displayMonth = setYear(state.displayMonth, year);
                }
                state.showYears = false;
            }
        };

        const calculatePickerPosition = () => {
            let inputDate = context.parent && context.parent.$refs.inputElement;
            if (inputDate instanceof HTMLInputElement) {
                if ((window.innerHeight - inputDate.getBoundingClientRect().bottom) < 360) {
                    if (window.innerHeight - inputDate.getBoundingClientRect().top >= 360) {
                        state.pickerPosition = 'top';
                    }
                }
                state.pickerPosition = 'bottom';
            }
        };

        onBeforeMount(() => {
            calculatePickerPosition();
        });

        return {
            state,
            headerTitle,
            calendarWeeks,
            calendarYears,
            daysNames,
            prevMonth,
            nextMonth,
            prevDecades,
            nextDecades,
            onPrevClick,
            onNextClick,
            onTitleClick,
            setSelectedDate,
            setSelectedYear,
            calendarDayClasses
        }
    }
})
